import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useGetExternalAgents } from "./api/useGetExternalAgents";
import { useDeleteExternalAgent } from "./api/useDeleteExternalAgent";
import { ExternalAgentModal } from "./components/ExternalAgentModal";

export default function AdminExternalAgents() {
  const { externalAgents, getExternalAgents, isLoading, isFailed } =
    useGetExternalAgents();
  const {
    deleteExternalAgent,
    isDeleted,
    isLoading: isDeleting,
  } = useDeleteExternalAgent();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<string | undefined>();

  useEffect(() => {
    getExternalAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isDeleted) return;
    getExternalAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleted]);

  const handleEdit = (agentName: string) => {
    setEditingAgent(agentName);
    setModalOpen(true);
  };

  const handleDelete = async (agentName: string) => {
    await deleteExternalAgent(agentName);
  };

  const handleCreate = () => {
    setEditingAgent(undefined);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    getExternalAgents();
    setModalOpen(false);
    setEditingAgent(undefined);
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      <h2 className="text-xl font-semibold mb-4">External Agents</h2>
      <p className="text-gray-600 mb-6">
        Manage external agents that can interact with the system.
      </p>

      <div className="mb-4">
        <Button onClick={handleCreate} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Agent
        </Button>
      </div>

      {isLoading && (
        <div className="text-center py-8">
          <p className="text-gray-500">Loading external agents...</p>
        </div>
      )}

      {isFailed && (
        <div className="text-center py-8">
          <p className="text-red-500">Failed to load external agents.</p>
        </div>
      )}

      {!isLoading && !isFailed && (
        <>
          {externalAgents && externalAgents.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent Name</TableHead>
                  <TableHead>Base URL</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {externalAgents.map((agent) => (
                  <TableRow key={agent.agentName}>
                    <TableCell className="font-medium">
                      {agent.agentName}
                    </TableCell>
                    <TableCell>{agent.baseUrl}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(agent.agentName)}
                          className="h-8 w-8 p-0"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              disabled={isDeleting}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete External Agent
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete the external
                                agent "{agent.agentName}"? This action cannot be
                                undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(agent.agentName)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">
                No external agents configured yet.
              </p>
            </div>
          )}
        </>
      )}

      <ExternalAgentModal
        open={modalOpen}
        onClose={handleModalClose}
        agentName={editingAgent}
      />
    </div>
  );
}
